import { useTranslation } from "react-i18next";
import type { AppointmentSummaryItem } from "@/lib/utils/appointment-summary-items";
import { cn } from "@/lib/utils";

interface AppointmentCardSummaryLinesProps {
  items: AppointmentSummaryItem[];
  maxItems?: number;
  className?: string;
  variant?: "default" | "kanban";
}

export function AppointmentCardSummaryLines({
  items,
  maxItems = 3,
  className,
  variant = "default",
}: AppointmentCardSummaryLinesProps) {
  const { t } = useTranslation("calendar");

  if (items.length === 0) return null;

  const visibleItems = items.slice(0, maxItems);
  const hiddenCount = items.length - visibleItems.length;

  const isKanban = variant === "kanban";

  return (
    <div className={cn(isKanban ? "mt-2 space-y-1" : "mt-1.5 space-y-0.5", className)}>
      {visibleItems.map((item) => (
        <div
          key={`${item.kind}-${item.id}`}
          className={cn(
            "flex min-w-0 items-center gap-1.5 leading-tight text-muted-foreground",
            isKanban ? "text-xs" : "text-[10px]"
          )}
        >
          <span className="shrink-0 font-medium">
            {item.kind === "package" ? t("tooltip.packagePill") : `${item.qty}x`}
          </span>
          <span className="min-w-0 truncate">{item.title}</span>
        </div>
      ))}
      {hiddenCount > 0 ? (
        <p 
          className={cn(
            "leading-tight text-muted-foreground",
            isKanban ? "pt-0.5 text-[10px]" : "text-[9px]"
          )}
        >
          {t("tooltip.moreItems", { count: hiddenCount })}
        </p>
      ) : null}
    </div>
  );
}
